import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const BarberProfileFix = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [linked, setLinked] = useState(false);

  const checkStatus = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/debug/my-barber-status');
      setStatus(response.data);
      setLinked(response.data.barber_profile_found);
      
      if (response.data.barber_profile_found) {
        toast.success('Your barber profile is already linked!');
      } else {
        toast.warning('Barber profile not linked. Click "Fix Now" to link it.');
      }
    } catch (error) {
      console.error('Error checking status:', error);
      toast.error('Failed to check status');
    } finally {
      setLoading(false);
    }
  };

  const linkProfile = async () => {
    setLoading(true);
    try {
      const response = await api.post('/api/debug/link-barber-profile');
      
      if (response.data.success) {
        toast.success('Profile linked successfully!', {
          description: 'Refreshing page...'
        });
        setLinked(true);
        
        // Refresh after 2 seconds
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        toast.error('Could not auto-link profile', {
          description: response.data.error
        });
        setStatus(response.data);
      }
    } catch (error) {
      console.error('Error linking profile:', error);
      toast.error('Failed to link profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white py-20 px-4">
      <div className="max-w-2xl mx-auto">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-2xl text-white flex items-center gap-2">
              🔧 Barber Profile Fix
            </CardTitle>
            <p className="text-zinc-400 text-sm mt-2">
              Fix your barber profile linking issue to enable schedule blocking and other features.
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* User Info */}
            <div className="bg-zinc-800/50 p-4 rounded-lg">
              <p className="text-sm text-zinc-400">Logged in as:</p>
              <p className="text-white font-semibold">{currentUser?.email}</p>
              <p className="text-zinc-400 text-sm">Role: {currentUser?.role}</p>
            </div>

            {/* Status Display */}
            {status && (
              <div className={`p-4 rounded-lg border ${
                linked 
                  ? 'bg-green-500/10 border-green-500/30' 
                  : 'bg-yellow-500/10 border-yellow-500/30'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {linked ? (
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-yellow-400" />
                  )}
                  <span className="font-semibold">
                    {linked ? 'Profile Linked ✅' : 'Profile Not Linked ⚠️'}
                  </span>
                </div>
                
                {status.barber_profile && (
                  <div className="mt-3 text-sm">
                    <p className="text-zinc-300">
                      Barber: <span className="text-white font-semibold">{status.barber_profile.name}</span>
                    </p>
                  </div>
                )}

                {!linked && status.available_barbers && status.available_barbers.length > 0 && (
                  <div className="mt-3 text-sm">
                    <p className="text-zinc-400">Available barber profiles:</p>
                    {status.available_barbers.map((barber, idx) => (
                      <p key={idx} className="text-white ml-2">• {barber.name}</p>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button
                onClick={checkStatus}
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Checking...
                  </>
                ) : (
                  'Check Profile Status'
                )}
              </Button>

              {status && !linked && (
                <Button
                  onClick={linkProfile}
                  disabled={loading}
                  className="w-full bg-lime-600 hover:bg-lime-700"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Linking...
                    </>
                  ) : (
                    '🔧 Fix Now - Link Profile'
                  )}
                </Button>
              )}

              {linked && (
                <Button
                  onClick={() => navigate('/barber-dashboard')}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  Go to Barber Dashboard →
                </Button>
              )}
            </div>

            {/* Instructions */}
            <div className="bg-zinc-800/30 p-4 rounded-lg text-sm">
              <p className="font-semibold text-white mb-2">What does this fix?</p>
              <ul className="text-zinc-400 space-y-1 list-disc list-inside">
                <li>Enables blocking dates and time slots</li>
                <li>Shows your appointments in barber dashboard</li>
                <li>Allows managing your schedule</li>
                <li>Fixes "Barber profile not found" errors</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BarberProfileFix;
